/**
 * Commander Design System - Shared Mantine Theme
 *
 * Base theme configuration shared between admin and kiosk applications.
 * IBM Carbon-inspired aesthetic with clean, professional styling.
 */

import { type MantineThemeOverride, createTheme, rem } from '@mantine/core';
import { colours, gray, blue, green, red, yellow } from '../tokens/colours';
import { fontFamily, fontWeight } from '../tokens/typography';

/**
 * Custom colour palette for Mantine
 * Maps our design tokens to Mantine's 10-shade colour system
 */
const mantineColours = {
  gray: [
    gray[10],
    gray[20],
    gray[30],
    gray[40],
    gray[50],
    gray[60],
    gray[70],
    gray[80],
    gray[90],
    gray[100],
  ],
  blue: [
    blue[10],
    blue[20],
    blue[30],
    blue[40],
    blue[50],
    blue[60],
    blue[70],
    blue[80],
    blue[90],
    blue[100],
  ],
  green: [
    green[10],
    green[20],
    green[30],
    green[40],
    green[50],
    green[60],
    green[70],
    green[80],
    green[90],
    green[100],
  ],
  red: [
    red[10],
    red[20],
    red[30],
    red[40],
    red[50],
    red[60],
    red[70],
    red[80],
    red[90],
    red[100],
  ],
  yellow: [
    yellow[10],
    yellow[20],
    yellow[30],
    yellow[40],
    yellow[50],
    yellow[60],
    yellow[70],
    yellow[80],
    yellow[90],
    yellow[100],
  ],
} as const;

export const sharedTheme: MantineThemeOverride = createTheme({
  /** Primary colour - blue for actions */
  primaryColor: 'blue',
  primaryShade: 5, // blue[60]

  /** Custom colour palette */
  colors: mantineColours,

  /** Typography */
  fontFamily: fontFamily.sans,
  fontFamilyMonospace: fontFamily.mono,

  /** Headings use semibold weight */
  headings: {
    fontFamily: fontFamily.sans,
    fontWeight: String(fontWeight.semibold),
    sizes: {
      h1: { fontSize: rem(36), lineHeight: '1.25' },
      h2: { fontSize: rem(30), lineHeight: '1.25' },
      h3: { fontSize: rem(24), lineHeight: '1.3' },
      h4: { fontSize: rem(20), lineHeight: '1.35' },
      h5: { fontSize: rem(18), lineHeight: '1.4' },
      h6: { fontSize: rem(16), lineHeight: '1.5' },
    },
  },

  /** Default radius for components */
  defaultRadius: 'md',

  /** Spacing scale */
  spacing: {
    xs: rem(4),
    sm: rem(8),
    md: rem(16),
    lg: rem(24),
    xl: rem(32),
  },

  /** Border radius scale */
  radius: {
    xs: rem(2),
    sm: rem(4),
    md: rem(8),
    lg: rem(12),
    xl: rem(16),
  },

  /** Shadows - subtle, professional */
  shadows: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
  },

  /** Component-specific overrides */
  components: {
    Button: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        root: {
          fontWeight: fontWeight.medium,
        },
      },
    },

    Card: {
      defaultProps: {
        radius: 'lg',
        withBorder: true,
      },
      styles: {
        root: {
          borderColor: colours.semantic.border,
        },
      },
    },

    Paper: {
      defaultProps: {
        radius: 'md',
      },
    },

    TextInput: {
      defaultProps: {
        radius: 'md',
      },
    },

    Select: {
      defaultProps: {
        radius: 'md',
      },
    },

    Table: {
      styles: {
        th: {
          fontWeight: fontWeight.semibold,
          color: colours.semantic.textHeading,
        },
      },
    },

    Badge: {
      defaultProps: {
        radius: 'sm',
      },
    },

    Modal: {
      defaultProps: {
        radius: 'lg',
      },
    },

    Drawer: {
      defaultProps: {
        radius: 0,
      },
    },

    Notification: {
      defaultProps: {
        radius: 'md',
      },
    },
  },
});
